import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { trpc, toastTrpcError } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";

export interface PersonaEditableData {
  id: string;
  name: string;
  role: string;
  brief: string | null;
  context: string | null;
  disposition: string | null;
}

interface EditPersonaDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  persona: PersonaEditableData;
}

export function EditPersonaDialog({
  open,
  onOpenChange,
  persona,
}: EditPersonaDialogProps) {
  const { id: projectId } = useParams<{ id: string }>();
  const utils = trpc.useUtils();

  const [name, setName] = useState(persona.name);
  const [role, setRole] = useState(persona.role);
  const [brief, setBrief] = useState(persona.brief ?? "");
  const [background, setBackground] = useState(persona.context ?? "");
  const [disposition, setDisposition] = useState(persona.disposition ?? "");

  useEffect(() => {
    if (open) {
      setName(persona.name);
      setRole(persona.role);
      setBrief(persona.brief ?? "");
      setBackground(persona.context ?? "");
      setDisposition(persona.disposition ?? "");
    }
  }, [open, persona]);

  const updateMutation = trpc.persona.update.useMutation({
    onSuccess: () => {
      toast.success("Persona updated");
      utils.persona.getById.invalidate({ id: persona.id });
      if (projectId) {
        utils.persona.list.invalidate({ projectId });
      }
      onOpenChange(false);
    },
    onError: (err) => toastTrpcError(err, "Failed to update persona"),
  });

  const firstName = persona.name.split(" ")[0];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Edit persona</DialogTitle>
          <DialogDescription>
            Update how {firstName} shows up across scenarios and walkthroughs.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-5">
          <div className="space-y-2">
            <Label htmlFor="edit-persona-name">Name</Label>
            <Input
              id="edit-persona-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="edit-persona-role">Role</Label>
            <Input
              id="edit-persona-role"
              value={role}
              onChange={(e) => setRole(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="edit-persona-brief">
              Brief{" "}
              <span className="font-normal text-muted-foreground">
                (optional — one-line summary shown in lists and pickers)
              </span>
            </Label>
            <Input
              id="edit-persona-brief"
              value={brief}
              onChange={(e) => setBrief(e.target.value)}
              placeholder="Shown in lists and pickers"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="edit-persona-background">
              Background{" "}
              <span className="font-normal text-muted-foreground">
                (optional)
              </span>
            </Label>
            <p className="text-xs text-muted-foreground">
              Durable working reality, environment/device context, and what they
              use today — never a one-off task.
            </p>
            <Textarea
              id="edit-persona-background"
              value={background}
              onChange={(e) => setBackground(e.target.value)}
              rows={6}
              placeholder="e.g. Travels to client sites by train 2–3 days a week, files expenses from a laptop on Friday afternoons, keeps paper receipts in a jacket pocket."
              className="text-base leading-relaxed"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="edit-persona-disposition">
              Disposition{" "}
              <span className="font-normal text-muted-foreground">
                (optional)
              </span>
            </Label>
            <p className="text-xs text-muted-foreground">
              Habits, patience level, and what makes them hesitate or abandon a
              flow.
            </p>
            <Textarea
              id="edit-persona-disposition"
              value={disposition}
              onChange={(e) => setDisposition(e.target.value)}
              rows={3}
              placeholder="e.g. Careful, avoids asking colleagues for help, abandons forms that require accounting codes."
              className="text-base leading-relaxed"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              updateMutation.mutate({
                id: persona.id,
                name: name.trim(),
                role: role.trim(),
                brief: brief.trim() || null,
                context: background.trim() || null,
                disposition: disposition.trim() || null,
              });
            }}
            disabled={!name.trim() || !role.trim() || updateMutation.isPending}
          >
            {updateMutation.isPending ? "Saving…" : "Save persona"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
